import { useState } from 'react';
import { Button } from '@indipia/ui-core';
import type { Recipient, RecipientType } from './types';
import { RecipientCard } from './RecipientCard';
import { RecipientAddRow } from './RecipientAddRow';

// ── 타입별 아이콘 ────────────────────────────────────────────────────────────
function CustomerIcon() {
  return (
    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-info text-white">
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
      </svg>
    </div>
  );
}

function PartnerIcon() {
  return (
    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-warning text-white">
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
      </svg>
    </div>
  );
}

function VendorIcon() {
  return (
    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success text-white">
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
      </svg>
    </div>
  );
}

const SECTIONS: {
  type: RecipientType;
  label: string;
  Icon: () => JSX.Element;
}[] = [
  { type: 'CUSTOMER', label: '고객', Icon: CustomerIcon },
  { type: 'PARTNER', label: '반장님', Icon: PartnerIcon },
  { type: 'VENDOR', label: '거래처', Icon: VendorIcon },
];

interface RecipientAreaProps {
  recipients: Recipient[];
  onAdd: (recipient: Omit<Recipient, 'key'>) => void;
  onRemove: (key: string) => void;
  onRemoveWorkType?: (key: string, workTypeCode: string) => void;
  /** 반장·거래처 직접 입력 금지(등록 파트너 검색 선택만) */
  customerOnlyDirectInput?: boolean;
  /** 보여줄 수신자 종류(좌: 고객 / 우: 반장님·거래처). 생략하면 전부 */
  types?: RecipientType[];
}

export function RecipientArea({ recipients, onAdd, onRemove, onRemoveWorkType, customerOnlyDirectInput, types }: RecipientAreaProps) {
  const [openType, setOpenType] = useState<RecipientType | null>(null);

  function handleAdd(recipient: Omit<Recipient, 'key'>) {
    onAdd(recipient);
    setOpenType(null);
  }

  return (
    <div className="space-y-4">
      {/* 헤더: 아이콘 + 제목 */}
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-xxl bg-primary-100 text-primary-600">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
          </svg>
        </div>
        <span className="text-sm font-semibold text-text-primary">받는 대상</span>
      </div>

      {/* 타입별 수신자 목록 — 항상 모든 섹션 표시, 각 섹션에서 바로 추가 */}
      <div className="divide-y divide-border-default">
        {SECTIONS.filter((s) => !types || types.includes(s.type)).map(({ type, label, Icon }) => {
          const group = recipients.filter((r) => r.recipientType === type);
          return (
            <div key={type} className="py-3 first:pt-0 last:pb-0">
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon />
                  <span className="text-xs font-medium text-text-secondary">{label}</span>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  onClick={() => setOpenType((v) => (v === type ? null : type))}
                >
                  + 추가
                </Button>
              </div>
              {group.length > 0 && (
                <div className="space-y-2">
                  {group.map((r) => (
                    <RecipientCard key={r.key} recipient={r} onRemove={onRemove} onRemoveWorkType={onRemoveWorkType} />
                  ))}
                </div>
              )}
              {openType === type && (
                <div className="mt-2">
                  <RecipientAddRow
                    type={type}
                    onAdd={handleAdd}
                    onCancel={() => setOpenType(null)}
                    customerOnlyDirectInput={customerOnlyDirectInput}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
